import React, { useMemo } from 'react';
import Style from './panelStyles.module.scss';
import clsx from 'clsx';
import { Property } from 'csstype';
import { useNode } from '@craftjs/core';
import { PanelContainer } from './uiKit/panelContainer/panelContainer';
import { ControlsGroup } from './uiKit/controlsGroup/controlsGroup';
import { SettingsInput } from './uiKit/input/settingsInput';
import { hasSection, clamp } from './helpers/helper';
import { SettingsToggleButton, SettingsToggleButtonItem } from './uiKit/toggleButton/toggleButton';
import { ReactComponent as Paddings } from 'assets/img/generalBuilder/paddings_12.svg';
import { ReactComponent as Gap } from 'assets/img/generalBuilder/gap_12.svg';
import { ReactComponent as Direction } from 'assets/img/generalBuilder/direction_24.svg';
import { ReactComponent as JustifyStart } from 'assets/img/generalBuilder/justify_start_24.svg';
import { ReactComponent as JustifyCenter } from 'assets/img/generalBuilder/justify_center_24.svg';
import { ReactComponent as JustifyEnd } from 'assets/img/generalBuilder/justify_end_24.svg';
import { ReactComponent as JustifyBetween } from 'assets/img/generalBuilder/justify_between_24.svg';
import { ReactComponent as AlignStart } from 'assets/img/generalBuilder/align_start_24.svg';
import { ReactComponent as AlignCenter } from 'assets/img/generalBuilder/align_center_24.svg';
import { ReactComponent as AlignEnd } from 'assets/img/generalBuilder/align_end_24.svg';
import { ReactComponent as AlignStretch } from 'assets/img/generalBuilder/align_stretch_24.svg';
import { ReactComponent as Width } from 'assets/img/generalBuilder/width_12.svg';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';

type LayoutPanelSection = 'Dimension' | 'Paddings' | 'ContentLayout' | 'VerticalAlignment' | 'HorizontalAlignment';

export type LayoutPanelProps = {
  sections: LayoutPanelSection[];
};

export const LayoutPanel = ({ sections }: LayoutPanelProps) => {
  const { t } = useScopedTranslation('GeneralFormBuilder.Panels.Layout');
  const {
    actions: { setCustom },
    styles,
  } = useNode((node) => ({
    styles: node.data.custom,
  }));

  //#region Options

  const contentLayoutOptions: SettingsToggleButtonItem<Property.FlexDirection>[] = useMemo(
    () => [
      {
        icon: <Direction />,
        value: 'column',
      },
      {
        icon: <Direction className={clsx(Style.commonIcon, Style.rotate270)} />,
        value: 'row',
      },
    ],
    [],
  );

  const justifyContentOptions: SettingsToggleButtonItem<Property.JustifyContent>[] = useMemo(
    () => [
      {
        icon: (
          <JustifyStart
            className={clsx(Style.commonIcon, {
              [Style.rotate270]: styles.flexDirection === 'row',
            })}
          />
        ),
        value: 'flex-start',
      },
      {
        icon: (
          <JustifyCenter
            className={clsx(Style.commonIcon, {
              [Style.rotate90]: styles.flexDirection === 'row',
            })}
          />
        ),
        value: 'center',
      },
      {
        icon: (
          <JustifyEnd
            className={clsx(Style.commonIcon, {
              [Style.rotate270]: styles.flexDirection === 'row',
            })}
          />
        ),
        value: 'flex-end',
      },
      {
        icon: (
          <JustifyBetween
            className={clsx(Style.commonIcon, {
              [Style.rotate90]: styles.flexDirection === 'row',
            })}
          />
        ),
        value: 'space-between',
      },
    ],
    [styles.flexDirection],
  );

  const contentAlignOptions: SettingsToggleButtonItem<Property.AlignItems>[] = useMemo(
    () => [
      {
        icon: (
          <AlignStretch
            className={clsx(Style.commonIcon, {
              [Style.rotate90]: styles.flexDirection === 'row',
            })}
          />
        ),
        value: 'stretch',
      },
      {
        icon: (
          <AlignStart
            className={clsx(Style.commonIcon, {
              [Style.rotate90]: styles.flexDirection === 'row',
            })}
          />
        ),
        value: 'flex-start',
      },
      {
        icon: (
          <AlignCenter
            className={clsx(Style.commonIcon, {
              [Style.rotate90]: styles.flexDirection === 'row',
            })}
          />
        ),
        value: 'center',
      },
      {
        icon: (
          <AlignEnd
            className={clsx(Style.commonIcon, {
              [Style.rotate90]: styles.flexDirection === 'row',
            })}
          />
        ),
        value: 'flex-end',
      },
    ],
    [styles.flexDirection],
  );

  //#endregion

  return (
    <PanelContainer header={t('Header')} panelName="Layout">
      {hasSection(sections, 'Dimension') && (
        <ControlsGroup header={t('Width')} contentClassName={Style.gridContainer}>
          <SettingsInput
            badge={<Width />}
            type={'number'}
            value={styles.width}
            units={'px'}
            onChange={(e) =>
              setCustom((prop: React.CSSProperties) => {
                prop.width = clamp(+e.target.value, 0, 1024);
              }, 500)
            }
          />
        </ControlsGroup>
      )}

      {hasSection(sections, 'Paddings') && (
        <ControlsGroup
          header={t('Paddings')}
          contentClassName={Style.gridContainer}
          collapsible={true}
          expanded={styles.paddingLeft !== undefined || styles.paddingTop !== undefined}
          onOpen={() => {
            setCustom((prop: React.CSSProperties) => {
              prop.paddingLeft = 12;
              prop.paddingRight = 12;
              prop.paddingTop = 12;
              prop.paddingBottom = 12;
            }, 500);
          }}
          onClose={() => {
            setCustom((prop: React.CSSProperties) => {
              prop.paddingLeft = undefined;
              prop.paddingRight = undefined;
              prop.paddingTop = undefined;
              prop.paddingBottom = undefined;
            }, 500);
          }}
        >
          <SettingsInput
            badge={<Paddings className={Style.commonIcon} />}
            type={'number'}
            defaultValue={styles.paddingLeft || 0}
            onChange={(e) =>
              setCustom((prop: React.CSSProperties) => {
                prop.paddingLeft = clamp(+e.target.value, 0, 100);
                prop.paddingRight = clamp(+e.target.value, 0, 100);
              }, 500)
            }
          />
          <SettingsInput
            badge={<Paddings className={Style.rotate90} />}
            type={'number'}
            defaultValue={styles.paddingTop || 0}
            onChange={(e) =>
              setCustom((prop: React.CSSProperties) => {
                prop.paddingTop = clamp(+e.target.value, 0, 100);
                prop.paddingBottom = clamp(+e.target.value, 0, 100);
              }, 500)
            }
          />
        </ControlsGroup>
      )}

      {hasSection(sections, 'ContentLayout') && (
        <>
          <ControlsGroup header={t('ContentLayout')} contentClassName={Style.container}>
            <SettingsToggleButton
              menuItems={contentLayoutOptions}
              defaultValue={contentLayoutOptions.find((x) => x.value === styles.flexDirection)?.value || 'column'}
              onSelect={(value) =>
                setCustom((prop: React.CSSProperties) => {
                  prop.flexDirection = value;
                }, 500)
              }
            />
          </ControlsGroup>

          <ControlsGroup
            header={t('Gap')}
            contentClassName={Style.gridContainer}
            collapsible={true}
            expanded={styles.gap !== undefined}
            onOpen={() => {
              setCustom((prop: React.CSSProperties) => {
                prop.gap = 12;
              }, 500);
            }}
            onClose={() => {
              setCustom((prop: React.CSSProperties) => {
                prop.gap = undefined;
              }, 500);
            }}
          >
            <SettingsInput
              badge={
                <Gap
                  className={clsx(Style.commonIcon, {
                    [Style.rotate90]: styles.flexDirection === 'row',
                  })}
                />
              }
              type={'number'}
              defaultValue={styles.gap || 0}
              onChange={(e) =>
                setCustom((prop: React.CSSProperties) => {
                  prop.gap = clamp(+e.target.value, 0, 100);
                }, 500)
              }
            />
          </ControlsGroup>

          {(hasSection(sections, 'VerticalAlignment') || hasSection(sections, 'HorizontalAlignment')) && (
            <ControlsGroup header={t('ContentAlignment')} contentClassName={Style.flexContainer}>
              {hasSection(sections, 'verticalAlignment') && (
                <SettingsToggleButton
                  menuItems={justifyContentOptions}
                  defaultValue={styles.justifyContent || justifyContentOptions[0].value}
                  onSelect={(value) =>
                    setCustom((prop: React.CSSProperties) => {
                      prop.justifyContent = value;
                    }, 500)
                  }
                />
              )}
              {hasSection(sections, 'HorizontalAlignment') && (
                <SettingsToggleButton
                  menuItems={contentAlignOptions}
                  defaultValue={styles.alignItems || contentAlignOptions[0].value}
                  onSelect={(value) =>
                    setCustom((prop: React.CSSProperties) => {
                      prop.alignItems = value;
                    }, 500)
                  }
                />
              )}
            </ControlsGroup>
          )}
        </>
      )}
    </PanelContainer>
  );
};
